import { Container } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type {
  DataTableRecord,
  DataTableType,
} from '@components/DataTable/DataTable';
import { TableTitle } from '@components/DataTable/DataTable.style';
import FilterButton from '@components/FilterButton';
import Icon from '@components/Icon/Icon';
import { ProcessStepper } from '@components/ProcessStepper/index';
import RecordCard from '@components/RecordCard';

import * as ROUTES from '@constants/routes';

import { NoRecordsWrapper } from '@layouts/Layouts/BigTitleLayout';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';

const messages = defineMessages({
  noRecords: {
    id: 'imagingTests.noRecords',
    defaultMessage: 'No Known Imaging & Tests Records',
  },
  imagesAvailable: {
    id: 'imagingTests.imagesAvailable',
    defaultMessage: 'Are Images Available?',
  },
  notesAvailable: {
    id: 'imagingTests.notesAvailable',
    defaultMessage: 'Are Notes Available?',
  },
  viewedStatus: {
    id: 'imagingTests.viewedStatus',
    defaultMessage: 'Viewed Status',
  },
});

const ImagingTestCardView: React.FC<{
  data: DiagnosticReportRecord[];
  displayData: DiagnosticReportRecord[];
  tableData: DataTableRecord[];
  onChange: (filteredItems: Record<string, DataTableType>[]) => void;
}> = ({ data, displayData, tableData, onChange: handleChange }) => {
  const { formatMessage } = useIntl();

  return (
    <Container maxWidth="sm">
      <TableTitle>
        {data.length > 0 && (
          <FilterButton
            fields={[
              {
                name: 'viewedStatus',
                title: formatMessage(messages.viewedStatus),
                style: 'boolean',
              },
              {
                name: 'imagesAvailable',
                title: formatMessage(messages.imagesAvailable),
                style: 'boolean',
              },
              {
                name: 'testType',
                title: 'Test Type',
              },
              {
                name: 'hasNotes',
                title: formatMessage(messages.notesAvailable),
                style: 'boolean',
              },
              {
                name: 'facilityName',
                title: 'Facility Name',
              },
              {
                name: 'date',
                title: 'Date',
                style: 'date',
              },
              {
                name: 'hcpName',
                title: 'Provider Name',
              },
            ]}
            items={tableData}
            onChange={(filteredItems) => handleChange(filteredItems)}
          />
        )}
      </TableTitle>
      <ProcessStepper
        allBlack
        stepNames={displayData.map((i) => {
          const hcpName = i.practitioner;
          const testType = i.typeName;
          return (
            <RecordCard
              bottomText={i.getTestDateStr()}
              key={i.id}
              noteDisplay={Boolean(i.recordNotes.length)}
              onClick={() => {
                navigate(`${ROUTES.IMAGING_TESTS}${i.id}`);
              }}
              recordSource={i.recordSource}
              secondaryIcon={i.hasImages ? <Icon name="images" /> : undefined}
              status={i.curationStatus}
              text={`${i.testName} ${hcpName ? `- ${hcpName}` : ''}`}
              topText={testType}
              viewed={i.viewedStatus}
            />
          );
        })}
        style={{ width: '100%' }}
      />

      {displayData.length === 0 && (
        <NoRecordsWrapper>{formatMessage(messages.noRecords)}</NoRecordsWrapper>
      )}
    </Container>
  );
};

export default ImagingTestCardView;
